import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api";
import type { RoleName, User } from "../types";

interface AuthContextValue {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  extendSession: () => Promise<void>;
  hasRole: (role: RoleName) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const TOKEN_KEY = "cqrm_token";
export const REFRESH_TOKEN_KEY = "cqrm_refresh_token";

function persistTokens(accessToken: string, refreshToken: string | null) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken !== null && refreshToken !== "") {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [refreshToken, setRefreshToken] = useState<string | null>(() =>
    localStorage.getItem(REFRESH_TOKEN_KEY),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const me = await api.getMe(token);
        if (!cancelled) {
          setUser(me);
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setToken(null);
          setRefreshToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);
    const nextRefreshToken = result.refresh_token ?? null;
    persistTokens(result.access_token, nextRefreshToken);
    setToken(result.access_token);
    setRefreshToken(nextRefreshToken);
    const me = await api.getMe(result.access_token);
    setUser(me);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  const extendSession = useCallback(async () => {
    if (!token) {
      throw new Error("Cannot extend session without an access token");
    }

    const result = await api.extendSession(token, refreshToken);
    const nextRefreshToken = result.refresh_token ?? refreshToken;
    persistTokens(result.access_token, nextRefreshToken);
    setToken(result.access_token);
    setRefreshToken(nextRefreshToken);
  }, [token, refreshToken]);

  const hasRole = useCallback(
    (role: RoleName) => {
      if (!user) {
        return false;
      }

      if (user.roles.includes("super_admin")) {
        return true;
      }

      return user.roles.includes(role);
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      token,
      refreshToken,
      loading,
      login,
      logout,
      extendSession,
      hasRole,
    }),
    [user, token, refreshToken, loading, login, logout, extendSession, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
